// Cases: TanStack Query hooks + type definitions for /api/cases.
//
// A case is the ONE row a matter has, from the first ring through signing and
// into litigation (TMF-512). What used to be a lead is a case whose funnel is
// still running: the arrival creates it, the person it talks to is a Contact
// it points at, and the workflow machinery (workflow_id, plan) lives on the
// project it embeds.
//
// WHERE A MATTER SITS IS A COLUMN NOW (TMF-360). `stage` is projected off the
// plan by the events consumer inside the transaction that commits the plan, so
// a list can group and filter without pulling a project and its task rows down
// per row. The board reads it; nothing on this side derives it.
//
// The TASK ROWS are still the only thing that says what a rep may DO. The
// workspace loads the case's view (which embeds the project, its tasks and the
// matter's retainers) and reads its affordances off those rows through
// caseFunnel below: `qualified` and `disposition` are capture PAYLOAD (what the
// rep wrote, and the wording a chip shows), never an affordance.
//
// Qualification and close-out are captured with a case PUT (the value write IS
// the task completion, published by the SERVER); the ordinary manual tasks move
// by event (POST /api/events). Both are asynchronous: the rows this module
// reads settle a consumer hop after the write that caused them, so a
// just-captured matter renders its previous position for a beat.

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import { ACCIDENT_TYPES, type AccidentType } from "~/lib/accidentType";
import { ANSWERS, type Answer } from "~/lib/answers";
import type { Contact } from "~/lib/contacts";
import type { ContactRole } from "~/lib/contactRoles";
import type { Disposition } from "~/lib/dispositions";
import { postTaskStateEvent, type CaseEvent, type EventState } from "~/lib/events";
import type { NULL_VALUE } from "~/lib/filters";
import { halFetch, HalError, type HalCollection, type HalResource } from "~/lib/hal";
import type { LeadSource } from "~/lib/leadSources";
import { MARKETING_SOURCES, type MarketingSource } from "~/lib/marketingSources";
import { OFFICES, type Office } from "~/lib/offices";
import type { Position } from "~/lib/positions";
import type { Retainer } from "~/lib/retainers";
import {
  HANDOFF_TASK_TYPE,
  RESOLVE_CONTACT_TASK_TYPE,
  RETRY_HANDOFF_TASK_TYPE,
  type Task,
  type TasksCollection,
} from "~/lib/tasks";
import { THREADS_KEY, type Thread } from "~/lib/threads";
import type { TriageTier } from "~/lib/triage";
import { WORKFLOWS_LIST_KEY, type ModuleMeta } from "~/lib/workflows";

/** The one workflow every matter runs. Mirrors workflow.CaseWorkflowName on
 *  the API: the settings link finds the graph by this name. */
export const CASE_WORKFLOW_NAME = "Case";

/** The Case plan's task types, verbatim from lib/workflow/intake.go, which
 *  still names the funnel's tasks. Only the manual ones the workspace drives
 *  and the ones the board times are named here.
 *
 *  The three CAPTURE rows stay module-private: acting on them is a case PUT
 *  rather than an event, so nothing outside this file may hold them. "Triage
 *  lead" is one of them, which is why it is deliberately absent from
 *  CHAIN_TASK_TYPES below, where it would render an event button that would be
 *  a second author of one fact. */
export const FIRST_CONTACT_TASK_TYPE = "Make first contact";
const TRIAGE_TASK_TYPE = "Triage lead";
const QUALIFY_TASK_TYPE = "Qualify lead";
export const CHOOSE_RETAINER_TASK_TYPE = "Choose retainer";
export const SEND_RETAINER_TASK_TYPE = "Send retainer";
export const SIGN_RETAINER_TASK_TYPE = "Sign retainer";
const CLOSE_OUT_TASK_TYPE = "Close out lead";

/** The manual chain a rep works, in the order it is worked. Qualification and
 *  close-out are NOT here (those are captured with a case PUT and the server
 *  completes their tasks), and neither are the send, the signature or the
 *  handoff: the first and last belong to the app and the middle one to the
 *  client (TMF-407, TMF-452). The choice is what the rep does, and the bar
 *  opens the retainer dialog on it rather than completing it: the POST creates
 *  the row and the engine completes the task. */
const CHAIN_TASK_TYPES = [FIRST_CONTACT_TASK_TYPE, CHOOSE_RETAINER_TASK_TYPE] as const;

// ── The funnel vocabulary ──────────────────────────────────────────────

/**
 * Every position a matter's funnel can be in, in funnel order, verbatim from
 * enums.Stage. The value IS the display string: a column header and a card
 * chip both render the stored value, so there is no code/label split.
 *
 * A matter that never arrived through the funnel (the Filevine-synced case)
 * carries null instead.
 */
export const STAGES = [
  "New",
  "Contacted",
  "Qualified",
  "Retainer Sent",
  "Signed",
  "Lost",
  "No Rep",
] as const;

export type Stage = (typeof STAGES)[number];

/** The two dead ends, kept apart on purpose: a rising lost rate is a
 *  performance problem and a rising no-rep rate is a decision, so the board
 *  never averages them into one pile. Mirrors enums.DeadEndStages. */
export const DEAD_END_STAGES: readonly Stage[] = ["Lost", "No Rep"];

/** Has this matter's funnel ended? The two dead ends are the only positions it
 *  never comes back from; "Signed" is the end of the funnel rather than a dead
 *  end beside it, because the handoff is still owed. */
export function isDeadEnd(stage: Stage | null): boolean {
  return stage !== null && DEAD_END_STAGES.includes(stage);
}

// ── The plan the case's project runs ──────────────────────────────────

/**
 * Module/task lifecycle states matching the Go-side `workflow.State`
 * constants. The terminal pair (complete + cancelled) is what the
 * frontend tests for to gray a phase out on the strip and to count
 * "active" modules for filter chips.
 */
export type PlanModuleStateValue =
  | ""
  | "todo"
  | "in progress"
  | "complete"
  | "cancelled"
  | "rejected"
  | "delayed";

/**
 * Per-module entry on the persisted plan. Mirrors the Go-side
 * `core.ModuleState` which embeds `*Module` alongside `state`. The
 * embedded module fields (type/subject/assignee) serialize inline so
 * the wire shape is `{type, subject?, assignee?, state}`.
 */
export interface PlanModuleState extends ModuleMeta {
  state: PlanModuleStateValue;
}

export const TERMINAL_MODULE_STATES: ReadonlySet<string> = new Set([
  "complete",
  "cancelled",
]);

/** A module name is "active" on a case when its plan state is set
 *  and is not terminal (complete / cancelled). Multiple modules can
 *  be active at once when a workflow runs phases in parallel. */
export function isModuleActive(state: PlanModuleStateValue | undefined): boolean {
  if (state === undefined || state === "") return false;
  return !TERMINAL_MODULE_STATES.has(state);
}

/**
 * Lifecycle plan persisted on the case's project. Mirrors the Go-side
 * `workflow.PlanState`. Module names are the Case graph's (Case, Intake,
 * Retainers, NCD).
 */
export interface CasePlan {
  modules: Record<string, PlanModuleState>;
  tasks: Record<string, PlanModuleStateValue>;
  one_time_triggers?: string[] | null;
}

/**
 * The workflow-instance supertype embedded on a case (TMF-170). The workflow
 * link and the lifecycle plan live here; the name a matter is listed under is
 * the case's own column (TMF-512), as are its other domain fields.
 *
 * The task rows ride along on the VIEW route only, which is what the workspace
 * reads a matter's affordances off. The list route embeds no project at all
 * (TMF-360), which is why every embed below is optional.
 */
export interface CaseProject {
  id: string;
  workflow_id: string;
  plan: CasePlan;
  _embedded?: {
    tasks?: Task[];
    /** The plan's events, oldest first. View route only. */
    events?: CaseEvent[];
  };
}

/** A matter's triage answers, question text to option text. Mirrors
 *  models.CaseTriage: the sentences the rep saw, not keys that need an
 *  instrument beside them to read. */
export type CaseTriage = Record<string, string>;

/**
 * The Case resource. The LIST route answers rows plus the person the matter
 * talks to, the one relation a matter list carries; the VIEW route
 * (GET /api/cases/:id) embeds that person too, together with the project and
 * its plan and task rows, the agreements it chose and its conversations.
 */
export type Case = HalResource<{
  id: string;
  project_id: string;
  /** What the matter is called wherever it is listed: the person it talks to,
   *  composed server-side and rewritten when that person's record is
   *  corrected. Its own column, so a list labels its rows without an embed. */
  name: string;
  /** The person the firm talks to about this matter: the caller, who may be a
   *  guardian rather than the client. Null on a matter that never arrived
   *  through the funnel. Identity is theirs, never the case's. */
  contact_id: string | null;
  /** The lead this one was opened from: a passenger's lead derived from the
   *  driver's (TMF-594). Null on every lead opened on its own. */
  derived_from_case_id: string | null;
  filevine_project_id: number | null;
  /** Where the matter sits, projected off its plan by the API. Null for a
   *  matter with no funnel, which is the Filevine-synced case. */
  stage: Stage | null;
  /** The arrival path, in the one closed vocabulary a source is expressed in
   *  (enums.LeadSource). The value is what the board's source chip renders,
   *  and it is null on a matter that never arrived through a channel. */
  source: LeadSource | null;
  notes: string;
  triage: CaseTriage | null;
  /** The qualification, DERIVED and stamped server-side whenever answers are
   *  written (TMF-277): the plain sum of the answered options' points on a
   *  0 to 100 scale, and the band it falls in. Both null until the matter is
   *  triaged. Nothing on this side computes them. */
  triage_score: number | null;
  triage_tier: TriageTier | null;
  /** How the intake ended, in the firm's own words. Null while it is live.
   *  "Signed" is the engine's, and the six no-path outcomes are the rep's,
   *  recorded with the disqualifying answer or with the close-out. */
  disposition: Disposition | null;
  /** The rep's yes/no answer to "Qualify lead" (models.Case.Qualified). Null
   *  until the matter has been qualified, and CAPTURE-ONCE after: the API
   *  refuses a change, so the workspace offers the question exactly once. A NO
   *  always carries its reason on `disposition`. */
  qualified: boolean | null;
  triaged_at: string | null;
  qualified_at: string | null;
  /** The incident (TMF-513). An answer nobody has given yet is null; a text
   *  nobody has filled in yet is "". `date_of_loss` is a timestamp on the
   *  view, written back as `YYYY-MM-DD`. */
  date_of_loss: string | null;
  time_of_loss: string;
  accident_type: AccidentType | null;
  accident_location: string;
  working_at_time: boolean | null;
  third_party_insurance: string;
  first_party_insurance: string;
  policy_number: string;
  client_policy_number: string;
  police_report: string;
  pd_description: string;
  medical_treatment: string;
  injuries: string;
  referred_by: string;
  quick_summary: string;
  office: Office | null;
  marketing_source: MarketingSource | null;
  /** The six intake sections (TMF-622), null or "" until given as above. */
  prior_attorney_represented: boolean | null;
  prior_attorney_name: string;
  prior_attorney_termination: string;
  witnesses: string;
  footage: boolean | null;
  footage_location: string;
  trip_purpose: string;
  vehicle_year: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_location: string;
  /** Dollars a day; null is no fee. */
  storage_fee: number | null;
  host_owner_name: string;
  host_insurer: string;
  host_policy_number: string;
  host_claim_number: string;
  footwear: string;
  substances: string;
  employees_nearby: string;
  dog_breed: string;
  animal_control_report: string;
  dog_owner_name: string;
  dog_owner_address: string;
  dog_owner_relationship: string;
  /** Derived server-side off the Client's birth date. Null when the matter has
   *  no Client, the Client has no birth date, or the row came from a list. */
  client_is_minor: boolean | null;
  created_by_id: string | null;
  updated_by_id: string | null;
  created_at: string;
  updated_at: string;
  _embedded?: {
    project?: CaseProject;
    /** The person this matter talks to. The workspace header, the card and the
     *  SMS sender all read their name and number off this, because none of it
     *  is a column here. Both routes: the board's card shows the caller's
     *  number, so the list carries it (TMF-512). Absent on a matter that never
     *  arrived through the funnel. */
    contact?: Contact;
    /** The agreements this matter was asked to sign, oldest first. PAYLOAD:
     *  the workspace reads the variant, the delivery and the state word off
     *  them, and reads every affordance off the task rows beside them. */
    retainers?: Retainer[];
    /** The matter's conversations, rows only: what was said on each is the
     *  messages resource's. */
    threads?: Thread[];
    /** The people on the matter with the part each plays, oldest first. View
     *  route only. */
    contacts?: CaseContact[];
    /** The lead this one was opened from, and the leads opened from this one,
     *  oldest first: rows only, for the links between them. View route only. */
    derived_from?: Case;
    derived_cases?: Case[];
  };
}>;

/**
 * One person's part on one matter. Mirrors models.CaseContact, which carries
 * no self-link: the rows are written through the case's `contacts` list.
 * `relationship` is "" on every role but a Decedent's or a Guardian's;
 * `position` is null until someone records where the person sat.
 */
export interface CaseContact {
  id: string;
  case_id: string;
  contact_id: string;
  role: ContactRole;
  relationship: string;
  position: Position | null;
  /** Derived server-side off the person's birth date. Null when it is
   *  unknown. */
  is_minor: boolean | null;
  created_at: string;
  updated_at: string;
  _embedded?: {
    contact?: Contact;
  };
}

export type CaseCollection = HalCollection<Case>;

/** The project a case runs on, or undefined when the case was read from the
 *  list, which embeds none (TMF-360). */
export function caseProject(c: Case): CaseProject | undefined {
  return c._embedded?.project;
}

/** Active module names from the plan, in iteration order. Empty when
 *  no module is in flight (case never started, or every phase ended). */
export function activeModules(plan: CasePlan): string[] {
  const out: string[] = [];
  for (const [name, entry] of Object.entries(plan.modules)) {
    if (isModuleActive(entry.state)) out.push(name);
  }
  return out;
}

// ── Queries ───────────────────────────────────────────────────────────

/**
 * Filters accepted by the cases list endpoint. Each `stage` value becomes its
 * own `stage=` param and the server ORs them, so one request fetches every
 * column the board draws (TMF-512). A value outside the vocabulary is a
 * validation error server-side rather than an empty list.
 */
export interface CaseListFilters {
  stage?: readonly Stage[];
  /** A case-insensitive contains match on the matter's name column. */
  name?: string;
  /** `null` lists only the matters linked to a Filevine project, which is the
   *  case-management set the Cases section shows (TMF-546). */
  filevine_project_id_ne?: typeof NULL_VALUE;
}

/**
 * Invalidation root for every cases-list cache entry. Mutations call
 * `invalidateQueries({ queryKey: CASES_LIST_KEY })`; TanStack Query's
 * default prefix-match takes care of every variant under
 * `["cases", "list", ...]`.
 */
export const CASES_LIST_KEY = ["cases"] as const;

/** The board renders every matter in the funnel at once (and its KPI tiles
 *  count them), so the page is fetched whole. `size` is goat's page-size
 *  param. */
const BOARD_PAGE_SIZE = 200;

export function casesListQueryOptions(filters: CaseListFilters = {}) {
  const url = buildCasesListUrl(filters);
  return queryOptions<CaseCollection>({
    queryKey: ["cases", "list", filters] as const,
    queryFn: ({ signal }) => halFetch<CaseCollection>(url, { signal }),
  });
}

function buildCasesListUrl(filters: CaseListFilters): string {
  const params = new URLSearchParams({ size: String(BOARD_PAGE_SIZE) });
  for (const stage of filters.stage ?? []) params.append("stage", stage);
  if (filters.name !== undefined) params.append("name", filters.name);
  if (filters.filevine_project_id_ne !== undefined) {
    params.append("filevine_project_id_ne", filters.filevine_project_id_ne);
  }
  return `/api/cases?${params.toString()}`;
}

/**
 * Every matter the funnel has a position for: the seven columns the board
 * draws, and so the collection the rail labels its slots from.
 *
 * No poll: the API signals `cases:all` on the SSE signals channel after every
 * write, a rep's edit or a call the telephony consumer just delivered, and
 * useEventChannel invalidates this key.
 */
export const boardCasesQueryOptions = casesListQueryOptions({ stage: STAGES });

export const caseQueryKey = (id: string) => ["cases", id] as const;

export const caseQueryOptions = (id: string) =>
  queryOptions<Case>({
    queryKey: caseQueryKey(id),
    queryFn: ({ signal }) => halFetch<Case>(`/api/cases/${id}`, { signal }),
  });

// ── Per-case projections (tasks + events) ────────────────────────────
//
// Thin wrappers over the canonical /api/tasks and /api/events list
// endpoints with a project_id filter applied. The wrappers exist to give
// the case-detail page a stable cache key (`["cases", id, "tasks"]`,
// `["cases", id, "events"]`) that the post-event invalidation path
// can target without having to know the precise list-filter shape, and
// to hide the HAL collection envelope from consumers.

const CASE_DETAIL_PAGE_SIZE = 200;

/** Wrapper response shape for the case detail page's task read. Mirrors the
 *  `{ tasks }` envelope the page consumes so call sites (and optimistic-update
 *  merges) stay unchanged. */
export interface CaseTasksResponse {
  tasks: Task[];
}

export const caseTasksQueryKey = (id: string) => ["cases", id, "tasks"] as const;

// Keyed by the URL case id (so the realtime ["cases", caseId] prefix
// invalidation still reaches it) but fetched by the case's project id:
// tasks are project-scoped (TMF-170).
export const caseTasksQueryOptions = (caseId: string, projectId: string) =>
  queryOptions<CaseTasksResponse>({
    queryKey: caseTasksQueryKey(caseId),
    queryFn: async ({ signal }) => {
      // sort=created_at&dir=asc overrides the API's newest-first default
      // so freshly-edited rows keep their position on optimistic-update
      // refetches instead of jumping to the top.
      const params = new URLSearchParams({
        project_id: projectId,
        size: String(CASE_DETAIL_PAGE_SIZE),
        sort: "created_at",
        dir: "asc",
      });
      const collection = await halFetch<TasksCollection>(
        `/api/tasks?${params.toString()}`,
        { signal },
      );
      return { tasks: collection._embedded ?? [] };
    },
  });

// ── Mutations ─────────────────────────────────────────────────────────

/**
 * Fields a case PUT accepts. Rep identity is stamped server-side, and so is
 * the matter's name: it follows the person the case points at, so there is no
 * name here to send. The four identity fields are ARRIVAL inputs only, refused
 * by name on an update pointing at /api/contacts, so they are absent too.
 *
 * Two of these fields ARE plan work: capturing `qualified` completes "Qualify
 * lead" and capturing `disposition` on a qualified matter completes "Close out
 * lead", both published by the SERVER off the write. This app never posts an
 * event for either.
 */
export interface CaseUpdate {
  /** Optional: omitted leaves the stored value alone; 0 unlinks the case from
   *  its Filevine project; a positive integer sets or replaces the link. Zero
   *  is not a valid Filevine project id, so it carries the clear gesture
   *  (TMF-376), and `null` reads as omitted. */
  filevine_project_id?: number;
  source?: LeadSource;
  notes?: string;
  /** Answers replace WHOLESALE, because a triage is one sitting rather than a
   *  field-by-field patch, so a caller recording one more answer sends the
   *  whole map. Validated server-side against models.IntakeTriage, which also
   *  stamps `triage_score` and `triage_tier` on the way through; an empty map
   *  clears all three. */
  triage?: CaseTriage;
  /**
   * How the intake ended, as one of the six client-writable outcomes:
   * "Signed" is the engine's to stamp when the retainer is signed, and
   * CLEARING is refused outright, because a recorded close-out is one-way
   * (TMF-335). There is no un-lose, and a caller who rings back opens a new
   * matter.
   *
   * It is legal in exactly two shapes: WITH `qualified: false`, where it is
   * the disqualifying answer's reason, and ALONE on a qualified matter with an
   * empty outcome, where it is the close-out capture.
   */
  disposition?: Disposition;
  /**
   * The qualification answer. Capture-once: sent once, on the matter the rep
   * just qualified. A NO must carry `disposition` in the SAME request.
   */
  qualified?: boolean;
  /** `YYYY-MM-DD`. "" clears this, `accident_type`, `working_at_time`,
   *  `office`, `marketing_source` and the text keys. Every incident key is
   *  refused once the matter has been pushed. */
  date_of_loss?: string;
  time_of_loss?: string;
  accident_type?: AccidentType | "";
  accident_location?: string;
  working_at_time?: Answer;
  third_party_insurance?: string;
  first_party_insurance?: string;
  policy_number?: string;
  client_policy_number?: string;
  police_report?: string;
  pd_description?: string;
  medical_treatment?: string;
  injuries?: string;
  referred_by?: string;
  quick_summary?: string;
  office?: Office | "";
  marketing_source?: MarketingSource | "";
  prior_attorney_represented?: Answer;
  prior_attorney_name?: string;
  prior_attorney_termination?: string;
  witnesses?: string;
  footage?: Answer;
  footage_location?: string;
  trip_purpose?: string;
  vehicle_year?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_location?: string;
  storage_fee?: string;
  host_owner_name?: string;
  host_insurer?: string;
  host_policy_number?: string;
  host_claim_number?: string;
  footwear?: string;
  substances?: string;
  employees_nearby?: string;
  dog_breed?: string;
  animal_control_report?: string;
  dog_owner_name?: string;
  dog_owner_address?: string;
  dog_owner_relationship?: string;
  /** The people on the matter as a SET: present REPLACES them, absent leaves
   *  them alone. */
  contacts?: CaseContactRequest[];
}

/** One row of a case write's `contacts` list. Mirrors models.CaseContactRequest. */
export interface CaseContactRequest {
  contact_id: string;
  role: ContactRole;
  relationship?: string;
  position?: Position;
}

/** The matter's free-text incident keys: written on a case PUT, and carried by
 *  a create. */
export const INCIDENT_TEXT = [
  "time_of_loss",
  "accident_location",
  "third_party_insurance",
  "first_party_insurance",
  "policy_number",
  "client_policy_number",
  "police_report",
  "pd_description",
  "medical_treatment",
  "injuries",
  "referred_by",
  "quick_summary",
  "prior_attorney_name",
  "prior_attorney_termination",
  "witnesses",
  "footage_location",
  "trip_purpose",
  "vehicle_year",
  "vehicle_make",
  "vehicle_model",
  "vehicle_location",
  "host_owner_name",
  "host_insurer",
  "host_policy_number",
  "host_claim_number",
  "footwear",
  "substances",
  "employees_nearby",
  "dog_breed",
  "animal_control_report",
  "dog_owner_name",
  "dog_owner_address",
  "dog_owner_relationship",
] as const;

/** The matter's yes / no / unknown incident keys: written on a case PUT, and
 *  carried by a create. */
export const INCIDENT_ANSWERS = ["working_at_time", "prior_attorney_represented", "footage"] as const;

export type IncidentAnswer = (typeof INCIDENT_ANSWERS)[number];

/** The person's free-text keys: written on a contact PUT, and carried by a
 *  create as arrival inputs. */
export const PERSON_TEXT = [
  "first_name",
  "last_name",
  "phone",
  "email",
  "address_street",
  "address_city",
  "address_state",
  "address_zipcode",
] as const;

const CASE_CREATE_TEXT = [...PERSON_TEXT, ...INCIDENT_TEXT, "date_of_birth", "date_of_loss"] as const;

/**
 * A rep's entry, POST /api/cases (TMF-568): the person, whom the API matches by
 * phone or email or creates, and what happened. The API requires the first
 * name, last name, phone and email. `date_of_birth` and `date_of_loss` are
 * `YYYY-MM-DD`.
 */
export type CaseCreate = Partial<Record<(typeof CASE_CREATE_TEXT)[number], string>> &
  Partial<Record<IncidentAnswer, Answer>> & {
  accident_type?: AccidentType;
  office?: Office;
  marketing_source?: MarketingSource;
};

/**
 * A passenger's lead, POST /api/cases (TMF-594): the lead they rode on and the
 * person on it. The API reads the person and the incident off that lead.
 */
export interface CaseDerive {
  derived_from_case_id: string;
  contact_id: string;
}

/** Every key a create carries, which is the name of the control that holds it. */
export const CASE_CREATE_KEYS: ReadonlySet<string> = new Set([
  ...CASE_CREATE_TEXT,
  ...INCIDENT_ANSWERS,
  "accident_type",
  "office",
  "marketing_source",
]);

/** A create form's named controls as the body the API takes. A blank value,
 *  and a choice outside its vocabulary, is left out. */
export function caseCreateOf(data: FormData): CaseCreate {
  const filled = (key: string): string | undefined => {
    const value = data.get(key);
    return typeof value === "string" && value.trim() !== "" ? value : undefined;
  };
  const body: CaseCreate = {};
  for (const key of CASE_CREATE_TEXT) {
    const value = filled(key);
    if (value !== undefined) body[key] = value;
  }
  const accidentType = ACCIDENT_TYPES.find((option) => option === filled("accident_type"));
  if (accidentType !== undefined) body.accident_type = accidentType;
  for (const key of INCIDENT_ANSWERS) {
    const answer = ANSWERS.find((option) => option.value === filled(key));
    if (answer !== undefined) body[key] = answer.value;
  }
  const office = OFFICES.find((option) => option === filled("office"));
  if (office !== undefined) body.office = office;
  const source = MARKETING_SOURCES.find((option) => option === filled("marketing_source"));
  if (source !== undefined) body.marketing_source = source;
  return body;
}

/** Opens a matter for a rep's entry or a passenger's lead. The API answers 201
 *  with the matter it opened, or 200 with the open matter the person already
 *  has. The invalidation reaches every case query, so the lead a passenger's
 *  was opened from re-reads its links. */
export function useCreateCaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CaseCreate | CaseDerive) => halFetch<Case>("/api/cases", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
    },
  });
}

export function useUpdateCaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CaseUpdate & { id: string }) =>
      halFetch<Case>(`/api/cases/${id}`, { method: "PUT", body }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<Case>(caseQueryKey(variables.id), updated);
      void queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
      // Matters ride embedded on threads too, so refresh both surfaces.
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

export function useDeleteCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      halFetch<null>(`/api/cases/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
      // Deleting a case removes it from the workflow's
      // _embedded.cases list and may unblock a pending workflow
      // delete in the danger zone (TMF-118).
      void queryClient.invalidateQueries({ queryKey: WORKFLOWS_LIST_KEY });
    },
  });
}

// ── What the plan says a rep may do ───────────────────────────────────

export interface StageWait {
  task: Task;
  title: string;
}

/** The manual row a stopped Handoff waits on: the contact decision a refusal
 *  grows, or the retry an error grows. */
export interface HandoffStop {
  kind: "decision" | "retry";
  task: Task;
}

/** The matter's live Handoff row, and what it is stopped on. `stop` is null
 *  while the push runs, once it completed, and for the engine hop before a
 *  stopped Handoff's manual row exists. */
export interface Handoff {
  task: Task;
  stop: HandoffStop | null;
}

/** The task types that carry a deadline worth showing on a card. */
export type WaitTaskType = typeof FIRST_CONTACT_TASK_TYPE | typeof SIGN_RETAINER_TASK_TYPE;

/** Which task's deadline a card's timer shows, per stage, declared once so a
 *  new stage has to say what (if anything) it is waiting on. ONE declaration:
 *  the board reads it against the task rows it lists, and the workspace reads
 *  it against the ones its case embeds. */
export const STAGE_WAIT_TASKS: Record<Stage, WaitTaskType | null> = {
  New: FIRST_CONTACT_TASK_TYPE,
  Contacted: null,
  Qualified: null,
  "Retainer Sent": SIGN_RETAINER_TASK_TYPE,
  Signed: null,
  Lost: null,
  "No Rep": null,
};

/** What each wait is counting, said in the rep's terms: the chip's tooltip. */
export const WAIT_TITLES: Record<WaitTaskType, string> = {
  [FIRST_CONTACT_TASK_TYPE]: "Time since the matter arrived, still untouched",
  [SIGN_RETAINER_TASK_TYPE]: "Time since the retainer went out, still unsigned",
};

/** The task rows the workspace acts on, read off the matter's plan.
 *
 *  Every field here is a statement about ROWS. Where the matter SITS is not
 *  here: `stage` is the case's own column (TMF-360), and the workspace reads
 *  it off the row it already holds. */
export interface CaseFunnel {
  /** The speed-to-lead task, the clock the firm is judged on. */
  firstContactTask: Task | null;
  /** The wait this matter's stage is on (TMF-443). Null in two cases: the
   *  stage has no wait task in the graph, or the plan has not composed that
   *  task's row yet. */
  wait: StageWait | null;
  /** The next open task of the MANUAL chain, which the workspace completes by
   *  event. Null when the matter's next move is a capture (qualification or
   *  close-out, both case PUTs) or when there is nothing left to do. */
  nextTask: Task | null;
  /** Is the qualification question live? Exactly one thing decides that: the
   *  "Qualify lead" row is present and open. Not the value beside it. The row
   *  closes a consumer hop AFTER the capture PUT answers, so the buttons
   *  persist for that beat. */
  asks: boolean;
  /** Is the triage instrument live? The "Triage lead" row exists and has not
   *  been cancelled, and the matter has not reached a terminal position. A
   *  COMPLETE triage stays editable, which is the point: correcting an answer
   *  re-completes the task, and the engine re-opens the decision off that
   *  completion (TMF-450). */
  triageable: boolean;
  /** Is the close-out live? Same rule, one row over: "Close out lead" is
   *  present and open.
   *
   *  It is open on a signed matter too, until "Handoff" cancels it, so the
   *  workspace offers it there and the API refuses that write, because a
   *  qualified matter's outcome belongs to its plan. The 422 surfaces through
   *  the action bar's Alert. */
  closeable: boolean;
  /** Did the agreement come back refused? The live "Sign retainer" row is
   *  REJECTED, which is what a decline and a vendor-side void both leave
   *  behind (TMF-407): the matter stays in Retainer Sent, its unsigned clock
   *  stops, and the header says so in a danger chip. */
  retainerRejected: boolean;
  /** Did the send fail for a reason no retry fixes? The "Send retainer" row is
   *  in the engine's error state, which is the plan's own statement that this
   *  agreement is not going out and the rep has to choose again (TMF-407). */
  sendFailed: boolean;
  /** May a retainer go out? The matter is qualified, nothing is signed and it
   *  has not reached a dead end. It stays true while one is already out,
   *  because sending again IS the resend: the choice supersedes the live
   *  row. */
  sendable: boolean;
  /** The push to Filevine. Null until the matter has a Handoff row that is not
   *  cancelled. */
  handoff: Handoff | null;
}

const OPEN_STATES = new Set(["todo", "in progress", "pending", "delayed"]);

/** Is this task row present AND still live? Absent rows and closed ones are
 *  the same answer to every question this module asks. */
function isOpen(task: Task | undefined): boolean {
  return task !== undefined && OPEN_STATES.has(task.state);
}

function isComplete(task: Task | undefined): boolean {
  return task?.state === "complete";
}

/** A rejected row is a verdict, not a closure: the decision a rep answered NO
 *  on. It is how a disqualified matter reads, since a NO never completes the
 *  task it answers. */
function isRejected(task: Task | undefined): boolean {
  return task?.state === "rejected";
}

/** An ERRORED row is work that cannot proceed: the send failed for a reason no
 *  retry fixes, and the row carries why. It holds across recomputes, so it is
 *  the plan's own statement rather than a moment that passes. */
function isErrored(task: Task | undefined): boolean {
  return task?.state === "error";
}

/** Is this row one a rep may still write to? Present, and not swept: a
 *  cancelled row is work the engine already foreclosed, whatever the rest of
 *  the plan says. */
function isEditable(task: Task | undefined): boolean {
  return task !== undefined && task.state !== "cancelled";
}

export function caseFunnel(caseRow: Case): CaseFunnel {
  // Task types are unique within the funnel, the same assumption
  // models.Project.TaskByType makes on the API side. A SUBJECTED type is not:
  // the retainer arm is grafted once per retainer row, so a matter sent two
  // agreements carries two of each of its three types (TMF-407). Cancelled
  // rows are skipped as work the engine foreclosed, and of what is left the
  // NEWEST row of a type wins, because the newest retainer is the arm the
  // matter is on. Rows arrive oldest first (the API orders the plan's tasks by
  // creation), so setting unconditionally keeps the last one seen.
  const tasksByType = new Map<string, Task>();
  for (const task of caseProject(caseRow)?._embedded?.tasks ?? []) {
    if (task.state === "cancelled") continue;
    tasksByType.set(task.type, task);
  }

  const firstContactTask = tasksByType.get(FIRST_CONTACT_TASK_TYPE) ?? null;
  const signRetainerTask = tasksByType.get(SIGN_RETAINER_TASK_TYPE) ?? null;
  const settled = isDeadEnd(caseRow.stage);

  return {
    firstContactTask,
    wait: stageWait(caseRow.stage, tasksByType),
    // MANUAL rows only. The send is System work and the signature is the
    // client's, so neither is ever a button in the action bar, whatever state
    // the plan leaves it in.
    nextTask:
      CHAIN_TASK_TYPES.map((type) => tasksByType.get(type) ?? null).find(
        (task) => task !== null && task.is_manual && OPEN_STATES.has(task.state),
      ) ?? null,
    asks: isOpen(tasksByType.get(QUALIFY_TASK_TYPE)),
    // The instrument closes where the matter does. Signed is not a dead end
    // (the handoff is still owed), but the API refuses a triage write on it
    // all the same, because the engine stamped the outcome.
    triageable:
      isEditable(tasksByType.get(TRIAGE_TASK_TYPE)) &&
      !settled &&
      caseRow.stage !== "Signed",
    closeable: isOpen(tasksByType.get(CLOSE_OUT_TASK_TYPE)),
    retainerRejected: isRejected(signRetainerTask ?? undefined),
    sendFailed: isErrored(tasksByType.get(SEND_RETAINER_TASK_TYPE)),
    sendable:
      isComplete(tasksByType.get(QUALIFY_TASK_TYPE)) &&
      !isComplete(signRetainerTask ?? undefined) &&
      !settled,
    handoff: handoffOf(tasksByType),
  };
}

function handoffOf(tasksByType: Map<string, Task>): Handoff | null {
  const task = tasksByType.get(HANDOFF_TASK_TYPE);
  if (task === undefined) return null;
  return { task, stop: handoffStop(task, tasksByType) };
}

function handoffStop(handoff: Task, tasksByType: Map<string, Task>): HandoffStop | null {
  if (isRejected(handoff)) return openStop("decision", tasksByType.get(RESOLVE_CONTACT_TASK_TYPE));
  if (isErrored(handoff)) return openStop("retry", tasksByType.get(RETRY_HANDOFF_TASK_TYPE));
  return null;
}

function openStop(kind: HandoffStop["kind"], task: Task | undefined): HandoffStop | null {
  if (task === undefined || !isOpen(task)) return null;
  return { kind, task };
}

function stageWait(stage: Stage | null, tasksByType: Map<string, Task>): StageWait | null {
  const taskType = stage === null ? null : STAGE_WAIT_TASKS[stage];
  if (taskType === null) return null;
  const task = tasksByType.get(taskType);
  if (task === undefined) return null;
  // A REJECTED wait is a wait that ended badly, not one still running: the
  // agreement came back declined or voided, and nobody is waiting on that
  // signature any more (TMF-407).
  if (isRejected(task)) return null;
  return { task, title: WAIT_TITLES[taskType] };
}

/** The project's page in the firm's Filevine, or null when the build carries
 *  no Filevine host. */
export function filevineProjectUrl(id: number): string | null {
  const base = import.meta.env.VITE_FILEVINE_WEB_URL;
  if (base === undefined || base === "") return null;
  return `${base.replace(/\/$/, "")}/#/project/${id}`;
}

// ── Chain moves ────────────────────────────────────────────────────────

export interface CaseTaskStateArgs {
  /** The matter's project: events are project-scoped. */
  projectId: string;
  taskId: string;
  state: EventState;
}

/**
 * Complete one of the matter's ORDINARY manual tasks: first contact, and the
 * retainer chain a qualified matter grows. The two CAPTURE tasks are not
 * reachable from here and must not be: "Qualify lead" and "Close out lead" are
 * completed by the server off the case PUT that records their value, so an
 * event posted from this side would be a second author of one fact.
 *
 * The engine processes the event asynchronously (queue to consumer), so the
 * refetch this fires can land before the plan has moved; the collections
 * settle on the next signal or refocus.
 */
export function useCaseTaskStateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: CaseTaskStateArgs) => postTaskStateEvent(variables),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

// ── Error shaping ─────────────────────────────────────────────────────

/**
 * True for 4XX HalErrors — caller-driven failures whose body the UI is
 * allowed to render verbatim. 5XX and non-HalError failures (network
 * drops, etc.) are treated as opaque by callers and must NOT leak the
 * original message to the client. Mirrors the validation-vs-server
 * error split established in the API services (see PR #37).
 */
export function isValidationError(err: unknown): err is HalError {
  return err instanceof HalError && err.status >= 400 && err.status < 500;
}

/** One refusal, keyed by the request field it is about, or by "" when the
 *  body names none. */
export interface FieldError {
  field: string;
  message: string;
}

/** What a refusal body says: the entries of an `{errors: [...]}` array, or the
 *  one string every other shape carries. */
type Problem = { kind: "errors"; entries: FieldError[] } | { kind: "text"; text: string };

/**
 * Read a goat validation error's body. goat returns either a single message or
 * a `{errors: [...]}` array. It also answers with an api-problem envelope
 * (`{describedBy, title, httpStatus, details}`), whose `details` carries the
 * reason and whose `title` is the bare status text, read in that order
 * (TMF-362). Falls back to the HTTP status code when the body matches no known
 * shape so the user always sees something actionable.
 */
function readProblem(err: HalError): Problem {
  const body = err.body;
  if (body !== null && typeof body === "object") {
    const record = body as Record<string, unknown>;
    const errors = record.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return {
        kind: "errors",
        entries: errors.map((e): FieldError => {
          if (typeof e === "string") return { field: "", message: e };
          if (e !== null && typeof e === "object") {
            const entry = e as Record<string, unknown>;
            const field = typeof entry.field === "string" ? entry.field : "";
            const message =
              typeof entry.message === "string"
                ? entry.message
                : typeof entry.error === "string"
                  ? entry.error
                  : "";
            return { field, message };
          }
          return { field: "", message: String(e) };
        }),
      };
    }
    if (typeof record.details === "string" && record.details.length > 0) {
      return { kind: "text", text: record.details };
    }
    if (typeof record.message === "string") return { kind: "text", text: record.message };
    if (typeof record.error === "string") return { kind: "text", text: record.error };
    if (typeof record.title === "string" && record.title.length > 0) {
      return { kind: "text", text: record.title };
    }
  }
  if (typeof body === "string" && body.length > 0) return { kind: "text", text: body };
  return { kind: "text", text: `Request rejected (${err.status}).` };
}

/** Render a goat validation error's body as one readable string. */
export function formatValidationError(err: HalError): string {
  const problem = readProblem(err);
  if (problem.kind === "text") return problem.text;
  return problem.entries
    .map(({ field, message }) =>
      field !== "" && message !== "" ? `${field}: ${message}` : message || field,
    )
    .filter((s) => s.length > 0)
    .join("; ");
}

// goat v0.2.3-alpha-22 errors.go:43-45 writes each field error as this prose.
const GOAT_FIELD_ERROR = /^(\S+) failed on validation '(.*)'$/s;

/**
 * A refusal as the fields it is about. A `details` string carries goat's
 * errors joined by "; ", each naming its field in goat's prose; a segment in
 * any other words is kept whole under field "".
 */
export function fieldErrors(err: HalError): FieldError[] {
  const problem = readProblem(err);
  if (problem.kind === "errors") {
    return problem.entries.filter(({ field, message }) => field !== "" || message !== "");
  }
  return problem.text.split("; ").map((segment): FieldError => {
    const match = GOAT_FIELD_ERROR.exec(segment);
    return match === null
      ? { field: "", message: segment }
      : { field: match[1] ?? "", message: match[2] ?? "" };
  });
}
